// src/pages/user-dashboard/index.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';

import AuditReportViewer from './components/AuditReportViewer';
import MetricsCard from './components/MetricsCard';
import SubmissionsTable from './components/SubmissionsTable';

// ── JSON extractor ────────────────────────────────────────────────────────────
function extractFromJSON(jsonData) {
  if (!jsonData) return null;
  const j = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
  const dims = j?.score_overview?.dimensions;
  return {
    score: j?.cover?.SCORE ?? 0,
    totalSlides: j?.cover?.TOTAL_SLIDES ?? 0,
    tierLabel: j?.cover?.TIER_LABEL ?? null,
    tierClass: j?.cover?.TIER_CLASS ?? null,
    industry: j?.cover?.INDUSTRY ?? null,
    companyName: j?.cover?.COMPANY_NAME ?? null,
    reportType: j?.report_type ?? 'FREE',
    fundingStage: j?.pitch_canvas?.FUNDING_STAGE ?? null,
    fundingAsk: j?.pitch_canvas?.FUNDING_ASK ?? null,
    priorityActions: (j?.all_issues || []).filter(i => i?.priority === 'High').length,
    marketFitScore: j?.competitor_benchmarks?.industry_average?.AVG_SCORE ?? null,
    subScores: {
      narrative: dims?.narrative?.DIM1_SCORE ?? 0,
      visual: dims?.visual_design?.DIM2_SCORE ?? 0,
      data: dims?.data_evidence?.DIM3_SCORE ?? 0,
      structure: dims?.structure_flow?.DIM4_SCORE ?? 0,
      persuasion: dims?.persuasion_cta?.DIM5_SCORE ?? 0,
    },
    dimTiers: {
      narrative: dims?.narrative?.DIM1_TIER ?? null,
      visual: dims?.visual_design?.DIM2_TIER ?? null,
      data: dims?.data_evidence?.DIM3_TIER ?? null,
      structure: dims?.structure_flow?.DIM4_TIER ?? null,
      persuasion: dims?.persuasion_cta?.DIM5_TIER ?? null,
    },
  };
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Sk = ({ h = 20, r = 8 }) => (
  <div className="sk-item" style={{ height: h, borderRadius: r }} />
);

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
@font-face {
  font-family: 'Sansation';
  src: local('Sansation Regular'), local('Sansation'), local('Sansation-Regular');
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: 'Sansation';
  src: local('Sansation Bold'), local('Sansation-Bold');
  font-weight: 700;
  font-style: normal;
}

:root {
  --page: #f2f2f7; --card: #ffffff;
  --card-border: rgba(0,0,0,0.04);
  --card-shadow: 0 0.5px 0 rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.04), 0 6px 16px rgba(0,0,0,0.02);
  --card-shadow-hover: 0 0.5px 0 rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.04);
  --hero-inner: #f8f9fa;
  --text-1: #1d1d1f; --text-2: #424245; --text-3: #86868b; --text-4: #aeaeb2; --text-inv: #ffffff;
  --blue: #004AAD; --blue-mid: #0069d9;

  /* ── Updated to match app-wide gradient ── */
  --blue-grad: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);

  --blue-soft: rgba(0,74,173,0.07); --blue-glow: rgba(0,100,220,0.10);
  --green: #1b8c2e; --green-soft: rgba(27,140,46,0.07); --green-text: #1b8c2e;
  --orange: #B5325B; --orange-soft: rgba(181,50,91,0.07); --orange-text: #B5325B;
  --yellow-text: #8a6d00; --yellow-soft: rgba(138,109,0,0.07);
  --ring-track: rgba(0,0,0,0.04); --divider: rgba(60,60,67,0.06);
  --input-bg: rgba(0,0,0,0.03); --label: #6e6e73;
  --ease: cubic-bezier(0.25, 0.1, 0.25, 1);
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }

.db-root { font-family: inherit; background: var(--page); min-height: 100vh; color: var(--text-1); }
.db-body { max-width: 1320px; margin: 0 auto; padding: 2px 40px 80px; }

/* ── HERO ── */
.db-hero {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 2px;
  margin-bottom: 2px;
  background: var(--card-border);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: var(--card-shadow);
  transition: box-shadow 0.4s;
  animation: db-rise 0.7s var(--ease) 0.1s both;
}
.db-hero:hover { box-shadow: var(--card-shadow-hover); }

/* ── SCORE CARD ── */
.score-card {
  background: var(--card);
  padding: 36px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.sc-title { font-family: inherit; font-size: 10px; font-weight: 600; color: var(--blue); letter-spacing: 0.18em; text-transform: uppercase; margin-bottom: 28px; }
.sc-badge { display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; border-radius: 100px; font-family: inherit; font-size: 11.5px; font-weight: 600; margin-bottom: 10px; }
.sc-badge-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.sc-desc { font-family: inherit; font-size: 13px; line-height: 1.5; color: var(--text-2); font-weight: 400; max-width: 250px; margin-bottom: 16px; }
.sc-tags { display: flex; flex-wrap: wrap; gap: 5px; justify-content: center; margin-bottom: 22px; }
.sc-tag { font-family: inherit; font-size: 11px; font-weight: 500; color: var(--text-3); padding: 4px 11px; border-radius: 100px; background: var(--input-bg); border: 0.5px solid var(--card-border); }
.sc-tag.gold { color: var(--yellow-text); background: var(--yellow-soft); border-color: transparent; font-weight: 600; }
.sc-tag.gold::before { content: '★ '; font-size: 9px; }
.sc-tag.blue-tag { background: var(--blue-soft); color: var(--blue); border-color: transparent; }
.sc-btn { font-family: 'Sansation', sans-serif; font-size: 14px; font-weight: 700; background: var(--blue-grad); color: var(--text-inv); border: none; border-radius: 12px; padding: 13px 24px; cursor: pointer; width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 2px 10px rgba(0,74,173,0.28); transition: all 0.25s var(--ease); }
.sc-btn:hover { transform: translateY(-1px); filter: brightness(1.08); box-shadow: 0 4px 18px rgba(0,74,173,0.38); }

.breakdown-card { background: var(--card); padding: 36px 36px 28px; display: flex; flex-direction: column; min-height: 380px; overflow: visible; }
.bk-title { font-family: 'Sansation', sans-serif; font-size: 18px; font-weight: 700; color: var(--text-1); margin-bottom: 0; letter-spacing: -0.02em; }
.bk-sub { font-family: inherit; font-size: 13px; color: var(--text-3); font-weight: 400; margin-top: 3px; }

/* ── METRICS ── */
.db-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  margin-bottom: 2px;
  background: var(--card-border);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: var(--card-shadow);
  transition: box-shadow 0.4s;
  animation: db-rise 0.7s var(--ease) 0.2s both;
}
.db-metrics:hover { box-shadow: var(--card-shadow-hover); }

.mc { background: var(--card); padding: 24px 26px 22px; display: flex; flex-direction: column; }
.mc-ico { width: 30px; height: 30px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.mc-ico.i-blue   { background: var(--blue-soft);  color: var(--blue); }
.mc-ico.i-orange { background: var(--orange-soft); color: var(--orange-text); }
.mc-ico.i-green  { background: var(--green-soft);  color: var(--green-text); }
.mc-ico.i-yellow { background: var(--yellow-soft); color: var(--yellow-text); }
.mc-lbl { font-family: inherit; font-size: 12.5px; font-weight: 500; color: var(--text-3); line-height: 1.4; }
.mc-num { font-family: 'Sansation', sans-serif; font-size: 38px; font-weight: 700; line-height: 1; letter-spacing: -0.04em; color: var(--text-1); margin-bottom: 4px; }
.mc-sub { font-family: inherit; font-size: 12.5px; color: var(--text-3); font-weight: 400; margin-bottom: 18px; }
.mc-foot { display: flex; gap: 0; border-top: 0.5px solid var(--divider); padding-top: 14px; margin-top: auto; }
.mc-kv { flex: 1; display: flex; flex-direction: column; gap: 3px; }
.mc-kv + .mc-kv { border-left: 0.5px solid var(--divider); padding-left: 14px; }
.mc-k { font-family: inherit; font-size: 9px; font-weight: 500; color: var(--text-4); letter-spacing: 0.1em; text-transform: uppercase; }
.mc-v { font-family: 'Sansation', sans-serif; font-size: 14.5px; font-weight: 700; color: var(--text-1); letter-spacing: -0.01em; }
.mc-v.green  { color: var(--green-text); }
.mc-v.blue   { color: var(--blue); }
.mc-v.orange { color: var(--orange-text); }
.mc-v.sm     { font-size: 13px; font-weight: 600; }
.suf         { font-size: 10.5px; font-weight: 500; color: var(--text-4); }

/* ── TABLE ── */
.tbl-wrap { background: var(--card); border-radius: 24px; overflow: hidden; box-shadow: var(--card-shadow); transition: box-shadow 0.4s; animation: db-rise 0.7s var(--ease) 0.3s both; }
.tbl-wrap:hover { box-shadow: var(--card-shadow-hover); }
.tbl-head-row { display: flex; align-items: center; justify-content: space-between; padding: 24px 32px 18px; gap: 12px; }
.tbl-title { font-family: 'Sansation', sans-serif; font-size: 18px; font-weight: 700; color: var(--text-1); letter-spacing: -0.02em; white-space: nowrap; }
.db-search { display: flex; align-items: center; gap: 8px; background: var(--input-bg); border: 0.5px solid var(--card-border); border-radius: 10px; padding: 8px 14px; width: 260px; transition: all 0.2s; flex-shrink: 0; }
.db-search:focus-within { border-color: var(--blue); box-shadow: 0 0 0 3px var(--blue-soft); }
.db-search svg { color: var(--text-4); flex-shrink: 0; }
.db-search input { border: none; outline: none; background: transparent; font-family: inherit; font-size: 13px; color: var(--text-1); width: 100%; min-width: 0; }
.db-search input::placeholder { color: var(--text-4); }
.db-table { width: 100%; border-collapse: collapse; }
.db-table thead th { font-family: inherit; font-size: 9.5px; font-weight: 600; color: var(--label); letter-spacing: 0.10em; text-transform: uppercase; text-align: left; padding: 11px 18px; border-top: 0.5px solid var(--divider); border-bottom: 0.5px solid var(--divider); background: var(--hero-inner); white-space: nowrap; cursor: pointer; user-select: none; transition: color .15s; }
.db-table thead th:hover { color: var(--blue); }
.db-table thead th:first-child { padding-left: 24px; }
.db-table thead th:last-child { padding-right: 32px; text-align: right; }
.db-table tbody td { padding: 18px 18px; font-size: 13.5px; color: var(--text-2); border-bottom: 0.5px solid var(--divider); vertical-align: middle; font-family: inherit; }
.db-table tbody tr:last-child td { border-bottom: none; }
.db-table tbody td:first-child { padding-left: 16px; }
.db-table tbody td:last-child { padding-right: 32px; text-align: right; }
.db-table tbody tr { transition: background 0.15s; cursor: pointer; }
.db-table tbody tr.data-row:hover td { background: var(--blue-soft); }
.db-table tbody tr.expand-row td { padding: 0; background: var(--hero-inner); }
.expand-btn { width: 26px; height: 26px; border-radius: 8px; border: 0.5px solid var(--card-border); background: var(--input-bg); color: var(--text-3); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; flex-shrink: 0; }
.expand-btn:hover { background: var(--blue-soft); color: var(--blue); border-color: transparent; }
.td-name { font-family: inherit; font-weight: 600; color: var(--text-1); font-size: 13.5px; line-height: 1.3; }
.td-hint { font-size: 11.5px; color: var(--text-4); margin-top: 2px; font-weight: 400; }
.td-mono { font-family: inherit; font-size: 11.5px; font-weight: 500; color: var(--text-2); letter-spacing: 0.3px; }
.td-date { font-family: inherit; font-size: 13px; font-weight: 400; color: var(--text-2); }
.td-eta  { font-family: inherit; font-size: 11px; font-weight: 500; color: var(--text-3); }
.badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 11px; border-radius: 100px; font-family: inherit; font-size: 11px; font-weight: 600; }
.badge::before { content: ''; width: 5px; height: 5px; border-radius: 50%; }
.badge.b-paid { background: var(--blue-soft); color: var(--blue); }
.badge.b-paid::before { background: var(--blue); }
.badge.b-free { background: var(--input-bg); color: var(--text-3); border: 0.5px solid var(--card-border); }
.badge.b-free::before { background: var(--text-3); }
.badge.b-done { background: var(--green-soft); color: var(--green-text); }
.badge.b-done::before { background: var(--green); }
.badge.b-pending { background: var(--orange-soft); color: var(--orange-text); }
.badge.b-pending::before { background: var(--orange-text); animation: db-pulse 1.5s infinite; }
.badge.b-processing { background: var(--blue-soft); color: var(--blue); }
.badge.b-processing::before { background: var(--blue); animation: db-pulse 1.5s infinite; }
.t-score { font-family: 'Sansation', sans-serif; font-size: 24px; font-weight: 700; letter-spacing: -0.03em; }
.t-score .ts { font-size: 12px; font-weight: 500; color: var(--text-4); }
.ts-good { color: #10F090; }
.ts-blue { color: #22D3EE; }
.ts-warn { color: #F59E0B; }
.ts-red  { color: #B5325B; }
.view-btn { display: inline-flex; align-items: center; gap: 6px; font-family: 'Sansation', sans-serif; font-size: 12.5px; font-weight: 700; color: #fff; background: var(--blue-grad); border: none; border-radius: 10px; padding: 8px 16px; cursor: pointer; box-shadow: 0 2px 8px rgba(0,74,173,0.22); transition: all 0.2s var(--ease); white-space: nowrap; }
.view-btn:hover { filter: brightness(1.1); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(0,74,173,0.34); }
.retry-btn { display: inline-flex; align-items: center; gap: 6px; font-family: 'Sansation', sans-serif; font-size: 12.5px; font-weight: 700; color: var(--orange-text); background: var(--orange-soft); border: none; border-radius: 10px; padding: 8px 16px; cursor: pointer; transition: all 0.2s var(--ease); }
.retry-btn:hover { background: var(--orange-text); color: var(--text-inv); transform: translateY(-1px); }

.info-wrap { display: inline-flex; align-items: center; position: relative; vertical-align: middle; margin-left: 4px; }
.info-ico { display: inline-flex; align-items: center; justify-content: center; cursor: default; color: rgba(0,74,173,0.35); transition: color 0.15s; line-height: 1; }
.info-ico:hover { color: var(--blue); }
.info-tip { position: absolute; top: calc(100% + 6px); right: 0; width: 168px; background: #1d1d1f; border-radius: 7px; padding: 8px 10px; pointer-events: none; opacity: 0; transition: opacity 0.12s; z-index: 9999; font-family: inherit !important; font-size: 11px !important; font-weight: 400 !important; text-transform: none !important; letter-spacing: normal !important; color: rgba(255,255,255,0.80) !important; line-height: 1.5 !important; text-align: left !important; white-space: normal !important; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.info-tip::before { content: ''; position: absolute; bottom: 100%; right: 4px; border: 4px solid transparent; border-bottom-color: #1d1d1f; }
.info-wrap:hover .info-tip { opacity: 1; }
.info-tip b { display: block; font-family: inherit !important; font-size: 11px !important; font-weight: 600 !important; color: #fff !important; text-transform: none !important; letter-spacing: normal !important; margin-bottom: 5px; }
.info-tip-row { display: flex; align-items: center; gap: 5px; margin-top: 3px; font-family: inherit !important; font-size: 10.5px !important; text-transform: none !important; letter-spacing: normal !important; font-weight: 400 !important; }
.info-tip-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }

/* ── WELCOME BANNER ── */
.wb-wrap {
  border-radius: 20px;
  background: linear-gradient(120deg, #dce9f8 0%, #e9f1fb 60%, #daeaf6 100%);
  padding: 28px 36px;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  animation: db-rise 0.6s var(--ease) 0.05s both;
  box-shadow: var(--card-shadow);
}
.wb-h { font-family: 'Sansation', sans-serif; font-size: 22px; font-weight: 700; color: var(--text-1); letter-spacing: -0.03em; margin-bottom: 6px; }
.wb-p { font-family: inherit; font-size: 13.5px; font-weight: 400; color: var(--text-2); line-height: 1.5; }
.wb-btn { display: inline-flex; align-items: center; gap: 8px; background: var(--blue-grad); color: #fff; border: none; border-radius: 100px; padding: 12px 24px; font-family: 'Sansation', sans-serif; font-size: 13.5px; font-weight: 700; cursor: pointer; white-space: nowrap; flex-shrink: 0; box-shadow: 0 4px 14px rgba(0,74,173,0.28); transition: all 0.22s var(--ease); }
.wb-btn:hover { filter: brightness(1.08); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(0,74,173,0.38); }
.wb-btn:active { transform: scale(0.98); }

/* ── MOBILE CARDS (for submissions table) ── */
.mobile-cards { display: none; }
.mob-card { padding: 16px 20px; border-bottom: 0.5px solid var(--divider); }
.mob-card:last-child { border-bottom: none; }
.mob-card-top { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px; }
.mob-card-name { font-family: inherit; font-size: 13.5px; font-weight: 600; color: var(--text-1); overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.mob-card-sub  { font-size: 11.5px; color: var(--text-4); margin-top: 2px; }
.mob-card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; margin-bottom: 12px; }
.mob-card-kv-k { font-family: inherit; font-size: 9px; font-weight: 500; color: var(--text-4); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 3px; }
.mob-card-kv-v { font-family: inherit; font-size: 13px; font-weight: 600; color: var(--text-1); }
.mob-card-actions { display: flex; gap: 8px; }

/* ── PENDING ── */
.db-pending { background: var(--card); border: 1.5px dashed rgba(60,60,67,0.12); border-radius: 24px; padding: 52px 24px; text-align: center; margin-bottom: 2px; animation: db-rise .4s var(--ease) both; box-shadow: var(--card-shadow); }
.db-pending-ico { font-size: 40px; margin-bottom: 14px; }
.db-pending-h { font-family: 'Sansation', sans-serif; font-size: 16px; font-weight: 700; color: var(--text-1); margin-bottom: 7px; }
.db-pending-p { font-family: inherit; font-size: 13px; color: var(--text-3); }

/* ── KEYFRAMES ── */
@keyframes db-spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes db-rise    { from { opacity: 0; transform: translateY(16px) scale(0.99); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes db-pulse   { 0%,100% { opacity: 1; } 50% { opacity: .4; } }
@keyframes db-breathe { 0%,100% { opacity: 0.4; transform: scale(0.95); } 50% { opacity: 1; transform: scale(1.08); } }
@keyframes db-expand  { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }

.sk-item {
  position: relative;
  overflow: hidden;
  background: var(--input-bg);
  width: 100%;
}
.sk-item::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent);
  transform: translateX(-100%);
  animation: db-shimmer 1.5s infinite;
}
@keyframes db-shimmer { 100% { transform: translateX(100%); } }

/* ═══════════════════════════════════════════════
   RESPONSIVE BREAKPOINTS
   ═══════════════════════════════════════════════ */

/* ── Large tablet / small desktop: 1024px ── */
@media (max-width: 1024px) {
  .db-hero {
    grid-template-columns: 300px 1fr;
  }
  .db-body {
    padding-left: 24px;
    padding-right: 24px;
  }
}

/* ── Tablet: 960px ── */
@media (max-width: 960px) {
  .db-metrics {
    grid-template-columns: repeat(2, 1fr);
    border-radius: 20px;
  }
}

/* ── Tablet portrait: 768px ── */
@media (max-width: 768px) {
  .db-body {
    padding: 2px 16px 60px;
  }

  /* Hero: stack vertically */
  .db-hero {
    grid-template-columns: 1fr;
    border-radius: 20px;
  }
  .score-card {
    padding: 28px 24px 24px;
    border-bottom: 0.5px solid var(--divider);
  }
  .breakdown-card {
    padding: 24px 20px 20px;
    min-height: unset;
  }

  /* Welcome banner: stack */
  .wb-wrap {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    border-radius: 16px;
    gap: 16px;
  }
  .wb-h { font-size: 18px; }
  .wb-p { font-size: 13px; }
  .wb-btn {
    width: 100%;
    justify-content: center;
    padding: 13px 20px;
  }

  /* Table: hide desktop, show mobile cards */
  .desktop-table { display: none; }
  .mobile-cards  { display: block; }

  /* Table header: stack */
  .tbl-head-row {
    flex-direction: column;
    align-items: stretch;
    padding: 16px 20px 14px;
    gap: 12px;
  }
  .db-search {
    width: 100%;
  }
  .tbl-wrap {
    border-radius: 20px;
  }

  /* Skeleton loading adjusts */
  .db-skeleton-hero {
    grid-template-columns: 1fr !important;
  }
  .db-skeleton-metrics {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

/* ── Mobile: 640px ── */
@media (max-width: 640px) {
  .db-body {
    padding: 2px 12px 56px;
  }

  /* Metrics: 2 columns still, tighter padding */
  .mc {
    padding: 18px 18px 16px;
  }
  .mc-num {
    font-size: 30px;
  }

  /* Welcome banner */
  .wb-h { font-size: 17px; }

  /* Table wrap */
  .tbl-title { font-size: 16px; }
  .tbl-head-row { padding: 14px 16px 12px; }

  /* Mob card */
  .mob-card { padding: 14px 16px; }

  /* Pending */
  .db-pending { padding: 40px 20px; border-radius: 20px; }
  .db-pending-ico { font-size: 32px; }
  .db-pending-h { font-size: 15px; }
}

/* ── Small mobile: 480px ── */
@media (max-width: 480px) {
  .db-metrics {
    grid-template-columns: 1fr 1fr;
    border-radius: 16px;
  }
  .mc {
    padding: 16px 14px 14px;
  }
  .mc-num { font-size: 26px; }
  .mc-lbl { font-size: 11.5px; }
  .mc-sub { font-size: 11.5px; margin-bottom: 12px; }
  .mc-k   { font-size: 8.5px; }
  .mc-v   { font-size: 13px; }

  .score-card { padding: 22px 16px 20px; }
  .sc-btn { font-size: 13px; padding: 11px 20px; }
  .sc-desc { font-size: 12px; }

  .breakdown-card { padding: 20px 16px 16px; }
  .bk-title { font-size: 15px; }

  .wb-wrap { padding: 16px; border-radius: 14px; }
  .wb-h { font-size: 16px; margin-bottom: 4px; }
  .wb-p { font-size: 12.5px; }
  .wb-btn { font-size: 13px; padding: 12px 16px; }

  .view-btn { font-size: 12px; padding: 7px 12px; }
  .retry-btn { font-size: 12px; padding: 7px 12px; }
  .mob-card-grid { grid-template-columns: 1fr 1fr; gap: 8px 12px; }
  .mob-card-name { font-size: 13px; }

  .badge { font-size: 10.5px; padding: 3px 9px; }
  .t-score { font-size: 20px; }
}

/* ── Very small: 360px ── */
@media (max-width: 360px) {
  .db-body { padding: 2px 10px 48px; }

  .db-metrics {
    grid-template-columns: 1fr;
    gap: 1px;
  }
  .mc { padding: 14px 14px 12px; }
  .mc-num { font-size: 32px; }

  .mob-card-grid { grid-template-columns: 1fr 1fr; }
  .mob-card-actions { flex-wrap: wrap; }
  .view-btn, .retry-btn { flex: 1; justify-content: center; font-size: 11.5px; }
}
`;

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const UserDashboard = () => {
  const { user, getClientForRole } = useAuth();
  const navigate = useNavigate();
  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'uploadDate', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('latest');

  const supabaseClient = useMemo(() => getClientForRole('customer'), [user?.id]);

  const visitKey = user?.id ? `mda_visited_${user.id}` : null;
  const [isFirstVisit] = useState(() => visitKey ? !localStorage.getItem(visitKey) : false);
  useEffect(() => { if (visitKey) localStorage.setItem(visitKey, '1'); }, [visitKey]);

  const formatDateToIST = (utcDateString) => {
    if (!utcDateString) return null;
    const date = new Date(utcDateString);
    return new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '—';
    const istDate = formatDateToIST(dateString);
    return istDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getETA = (item) => {
    if (item.decxScore > 0) return null;
    if (!item.uploadDate) return '18–24 hrs';
    const submitted = new Date(item.uploadDate);
    const eta = new Date(submitted.getTime() + 24 * 60 * 60 * 1000);
    const now = new Date();
    if (eta < now) return 'Soon';
    const diffH = Math.ceil((eta - now) / (1000 * 60 * 60));
    return diffH <= 1 ? '< 1 hr' : `~${diffH} hrs`;
  };

  useEffect(() => { if (user?.id) load(); }, [user]);

  const load = async () => {
    try {
      setIsLoading(true);
      const supabase = getClientForRole('customer');

      const { data: auditData, error: auditError } = await supabase
        .from('audit_reports')
        .select(`id, request_id, report_id, user_id, status, published_at, total_score, json_data, html_url, company_name, deck_name, report_type, created_at, updated_at, slides_analyzed, priority_actions, submission_id`)
        .eq('user_id', user?.id).eq('status', 'published').not('published_at', 'is', null)
        .order('published_at', { ascending: false });
      if (auditError) throw auditError;

      const { data: submissionData, error: submissionError } = await supabase
        .from('deck_submissions')
        .select(`id, title, file_name, status, priority, audit_type, submitted_at, created_at, updated_at, industry, audience, purpose, notes, slide_count, payment_order_id, request_id`)
        .eq('user_id', user?.id).order('submitted_at', { ascending: false });
      if (submissionError) throw submissionError;

      const auditBySubmissionId = {};
      (auditData || []).forEach(audit => { if (audit.submission_id) auditBySubmissionId[audit.submission_id] = audit; });

      const combined = [];

      (submissionData || []).forEach(sub => {
        const audit = auditBySubmissionId[sub.id];
        if (audit) {
          const jd = typeof audit.json_data === 'string' ? JSON.parse(audit.json_data) : audit.json_data;
          const ex = extractFromJSON(jd);
          combined.push({
            id: audit.id, auditId: audit.id, reportId: audit.report_id,
            requestId: audit.request_id || sub.request_id || `REQ-${sub.id.substring(0, 8)}`,
            presentationName: sub.title || sub.file_name || 'Audit Report',
            fileType: sub.file_name ? sub.file_name.split('.').pop()?.toUpperCase() : 'PPTX',
            uploadDate: audit.published_at || sub.submitted_at, status: 'completed',
            reportType: audit.report_type || ex?.reportType || sub.audit_type || 'free',
            isPaid: audit.report_type?.toLowerCase() === 'paid' || ex?.reportType === 'PAID' || sub.audit_type === 'paid',
            decxScore: audit.total_score || ex?.score || 0,
            subScores: ex?.subScores || {}, slidesAnalyzed: audit.slides_analyzed || ex?.totalSlides || sub.slide_count || 0,
            priorityActions: audit.priority_actions ?? ex?.priorityActions ?? 0,
            marketFitScore: ex?.marketFitScore || 0, tierLabel: ex?.tierLabel || null,
            companyName: audit.company_name || ex?.companyName || null,
            industry: ex?.industry || sub.industry || null, fundingStage: ex?.fundingStage || null,
            fundingAsk: ex?.fundingAsk || null, dimTiers: ex?.dimTiers || {}, isAudit: true, htmlUrl: audit.html_url,
          });
        } else {
          combined.push({
            id: sub.id, auditId: null, reportId: null,
            requestId: sub.request_id || `REQ-${sub.id.substring(0, 8)}`,
            presentationName: sub.title || sub.file_name || 'Untitled Submission',
            fileType: sub.file_name ? sub.file_name.split('.').pop()?.toUpperCase() : 'PPTX',
            uploadDate: sub.submitted_at, status: sub.status,
            reportType: sub.audit_type || 'free', isPaid: sub.audit_type === 'paid',
            decxScore: 0, subScores: {}, slidesAnalyzed: sub.slide_count || 0,
            priorityActions: 0, marketFitScore: 0, tierLabel: null, companyName: null,
            industry: sub.industry || null, fundingStage: null, fundingAsk: null,
            dimTiers: {}, isAudit: false,
          });
        }
      });

      (auditData || []).forEach(audit => {
        if (!audit.submission_id || !submissionData?.some(s => s.id === audit.submission_id)) {
          const jd = typeof audit.json_data === 'string' ? JSON.parse(audit.json_data) : audit.json_data;
          const ex = extractFromJSON(jd);
          combined.push({
            id: audit.id, auditId: audit.id, reportId: audit.report_id,
            requestId: audit.request_id || jd?.meta?.REQUEST_ID || null,
            presentationName: audit.deck_name || ex?.companyName || 'Audit Report',
            fileType: 'PPTX', uploadDate: audit.published_at || audit.created_at, status: 'completed',
            reportType: audit.report_type || ex?.reportType || 'free',
            isPaid: audit.report_type?.toLowerCase() === 'paid' || ex?.reportType === 'PAID',
            decxScore: audit.total_score || ex?.score || 0, subScores: ex?.subScores || {},
            slidesAnalyzed: audit.slides_analyzed || ex?.totalSlides || 0,
            priorityActions: audit.priority_actions ?? ex?.priorityActions ?? 0,
            marketFitScore: ex?.marketFitScore || 0, tierLabel: ex?.tierLabel || null,
            companyName: audit.company_name || ex?.companyName || null,
            industry: ex?.industry || null, fundingStage: ex?.fundingStage || null,
            fundingAsk: ex?.fundingAsk || null, dimTiers: ex?.dimTiers || {},
            isAudit: true, htmlUrl: audit.html_url,
          });
        }
      });

      combined.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
      setAllItems(combined);
    } catch (err) {
      console.error('❌ Dashboard load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    const sb = getClientForRole('customer');
    if (!sb) return;
    const auditChannel = sb.channel('audit_reports_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'audit_reports', filter: `user_id=eq.${user.id}` }, load).subscribe();
    const submissionChannel = sb.channel('deck_submissions_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deck_submissions', filter: `user_id=eq.${user.id}` }, load).subscribe();
    return () => { sb.removeChannel(auditChannel); sb.removeChannel(submissionChannel); };
  }, [user?.id]);

  const completed = useMemo(() => allItems.filter(item => item.decxScore > 0), [allItems]);
  const queueCount = useMemo(() => allItems.filter(item => item.decxScore === 0 && !item.isAudit).length, [allItems]);

  const filterOpts = useMemo(() => {
    const opts = [{ value: 'latest', label: 'Latest Report' }];
    completed.forEach(r => { const d = formatDisplayDate(r.uploadDate); opts.push({ value: r.id, label: `${r.presentationName} — ${d}` }); });
    return opts;
  }, [completed]);

  const activeReport = useMemo(() =>
    selectedFilter === 'latest' ? completed[0] ?? null : completed.find(r => r.id === selectedFilter) ?? null,
    [selectedFilter, completed]);

  const rd = useMemo(() => activeReport ? {
    score: activeReport.decxScore, totalSlides: activeReport.slidesAnalyzed,
    priorityActions: activeReport.priorityActions, marketFitScore: activeReport.marketFitScore,
    subScores: activeReport.subScores, tierLabel: activeReport.tierLabel,
    companyName: activeReport.companyName || activeReport.presentationName,
    fundingStage: activeReport.fundingStage, fundingAsk: activeReport.fundingAsk,
    reportType: activeReport.reportType, industry: activeReport.industry,
  } : null, [activeReport]);

  const tableData = useMemo(() =>
    [...allItems]
      .filter(r => !searchQuery
        || r.presentationName?.toLowerCase().includes(searchQuery.toLowerCase())
        || (r.requestId && r.requestId.toLowerCase().includes(searchQuery.toLowerCase()))
        || r.companyName?.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => {
        let av = a[sortConfig.key] ?? '', bv = b[sortConfig.key] ?? '';
        if (sortConfig.key === 'uploadDate') { av = new Date(av); bv = new Date(bv); }
        if (sortConfig.key === 'decxScore') { av = av || 0; bv = bv || 0; }
        return sortConfig.direction === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
      }),
    [allItems, sortConfig, searchQuery]);

  const doSort = k => setSortConfig(p => ({ key: k, direction: p.key === k && p.direction === 'asc' ? 'desc' : 'asc' }));

  const handleViewReport = (item) => {
    if (item.isAudit) navigate(`/audit-results-screen?reportId=${item.auditId || item.id}`);
    else alert('This report is still being processed. Please check back later.');
  };

  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'there';

  // Responsive skeleton
  const LoadingSkeleton = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'var(--sk-hero-cols, 340px 1fr)',
        gap: 16,
      }}
        className="db-skeleton-hero"
      >
        <Sk h={420} r={16} /><Sk h={420} r={16} />
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'var(--sk-metrics-cols, repeat(4,1fr))',
        gap: 16,
      }}
        className="db-skeleton-metrics"
      >
        {[1, 2, 3, 4].map(i => <Sk key={i} h={180} r={14} />)}
      </div>
      <Sk h={300} r={14} />
    </div>
  );

  return (
    <>
      <style>{CSS}</style>
      <Helmet><title>Dashboard — DecX AI</title></Helmet>

      <div className="db-root">
        <RoleBasedHeader />
        <AnnouncementBanner client={supabaseClient} />

        <div className="db-body">
          {/* Welcome Banner */}
          <div className="wb-wrap">
            <div>
              <div className="wb-h">
                {isFirstVisit ? `Welcome, ${firstName}! 👋` : `Welcome back, ${firstName}! 👋`}
              </div>
              <p className="wb-p">
                {isFirstVisit
                  ? "You're all set! Upload your first deck and get a professional DecX Score analysis."
                  : "Your deck intelligence dashboard is ready. Let's transform your next presentation."
                }
              </p>
            </div>
            <button className="wb-btn" onClick={() => navigate('/initial-form-upload-screen')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload New Deck
            </button>
          </div>

          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {rd ? (
                <AuditReportViewer
                  rd={rd}
                  activeReport={activeReport}
                  filterOpts={filterOpts}
                  selectedFilter={selectedFilter}
                  setSelectedFilter={setSelectedFilter}
                  onViewReport={() => activeReport?.auditId && navigate(`/audit-results-screen?reportId=${activeReport.auditId}`)}
                />
              ) : allItems.length > 0 ? (
                <div className="db-pending">
                  <div className="db-pending-ico">⏳</div>
                  <div className="db-pending-h">Your audit is being processed</div>
                  <div className="db-pending-p">{queueCount} submission{queueCount !== 1 ? 's' : ''} in queue · ETA 18–24 hours</div>
                </div>
              ) : null}

              <MetricsCard
                activeReport={activeReport}
                allItems={allItems}
                completed={completed}
                queueCount={queueCount}
                rd={rd}
                formatDisplayDate={formatDisplayDate}
              />

              {allItems.length > 0 ? (
                <SubmissionsTable
                  tableData={tableData}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  sortConfig={sortConfig}
                  doSort={doSort}
                  formatDisplayDate={formatDisplayDate}
                  getETA={getETA}
                  onViewReport={handleViewReport}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default UserDashboard;